import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: "#111827",
        border: "none",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
      }}
    >
      <p style={{ margin: "0 0 4px", fontFamily: "Nunito", fontSize: 11, color: "#9ca3af", fontWeight: 700 }}>
        {label}
      </p>
      <p style={{ margin: 0, fontFamily: "Nunito", fontSize: 16, fontWeight: 800, color: "#fff" }}>
        ${Number(payload[0].value).toLocaleString("en-US", { minimumFractionDigits: 2 })}
      </p>
    </div>
  );
}

function SkeletonChart() {
  return (
    <div className="stripe-chart-card">
      <div className="stripe-skeleton" style={{ width: 180, height: 18, borderRadius: 6, marginBottom: 20 }} />
      <div className="stripe-skeleton" style={{ width: "100%", height: 280, borderRadius: 10 }} />
    </div>
  );
}

const toUnix = (d, endOfDay) => {
  if (!d) return null;
  const dt = new Date(d);
  if (endOfDay) dt.setHours(23, 59, 59, 999);
  return Math.floor(dt.getTime() / 1000);
};

export default function MRRChart({ startDate, endDate }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    let url = "/api/stripe/mrr-chart";
    const s = toUnix(startDate, false);
    const e = toUnix(endDate, true);
    const params = [];
    if (s) params.push(`startDate=${s}`);
    if (e) params.push(`endDate=${e}`);
    if (params.length) url += `?${params.join("&")}`;

    axios
      .get(url)
      .then((res) => {
        if (res.data.success) setData(res.data.data);
      })
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <SkeletonChart />;

  return (
    <div className="stripe-chart-card">
      <p className="stripe-chart-title">
        {startDate || endDate ? "Monthly Revenue — Selected Range" : "Monthly Revenue — Last 12 Months"}
      </p>
      {data.length === 0 ? (
        <div className="stripe-empty">
          <div className="stripe-empty-icon">📊</div>
          No invoice data for the last 12 months
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 4, right: 16, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="mrrGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#111827" stopOpacity={0.06} />
                <stop offset="95%" stopColor="#111827" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontFamily: "Nunito", fontSize: 11, fontWeight: 700, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontFamily: "Nunito", fontSize: 11, fontWeight: 700, fill: "#9ca3af" }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`}
              width={52}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#e5e7eb", strokeWidth: 1 }} />
            <Area
              type="monotone"
              dataKey="mrr"
              name="Revenue"
              stroke="#111827"
              strokeWidth={2}
              fill="url(#mrrGradient)"
              dot={false}
              activeDot={{ r: 4, fill: "#111827", stroke: "#fff", strokeWidth: 2 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
